import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapCommissionPlanResponseToDto,
  getCommissionPlanUidFromResponse,
  buildCommissionPlanCreatePayload,
  buildCommissionPlanUpdatePayload,
} from '../dto/commission-plan-details.dto.js';
import { buildEmptyPlanForm, buildPlanFormFromDto, buildEmptyTier } from '../model/commission-plan-details.model.js';

export function useCommissionPlanDetails() {
  const { token } = useAuth();
  const { uid } = useParams();
  const navigate = useNavigate();
  const isCreateMode = uid === 'new';
  const [form, setForm] = useState(buildEmptyPlanForm());
  const [isLoading, setIsLoading] = useState(!isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCommissionRecalculationModal, setShowCommissionRecalculationModal] = useState(false);
  const [commissionRecalculationError, setCommissionRecalculationError] = useState('');
  const planRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const loadPlan = useCallback(async () => {
    const requestId = planRequestIdRef.current + 1;
    planRequestIdRef.current = requestId;

    if (isCreateMode) {
      setIsLoading(false);
      return;
    }
    if (!uid) {
      setError('Commission plan uid is required');
      setIsLoading(false);
      return;
    }
    setIsLoading(true); setError('');
    try {
      const response = await adminApi.getCommissionPlan(token, uid);
      if (!isMountedRef.current || planRequestIdRef.current !== requestId) return;
      const dto = mapCommissionPlanResponseToDto(response);
      setForm(buildPlanFormFromDto(dto));
    } catch (requestError) {
      if (!isMountedRef.current || planRequestIdRef.current !== requestId) return;
      setError(requestError.message || 'Failed to load commission plan');
    } finally {
      if (!isMountedRef.current || planRequestIdRef.current !== requestId) return;
      setIsLoading(false);
    }
  }, [uid, token, isCreateMode]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  useEffect(() => () => {
    isMountedRef.current = false;
    planRequestIdRef.current += 1;
  }, []);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onTierChange = useCallback((tierKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier) => tier.key === tierKey ? { ...tier, [field]: value } : tier),
    }));
  }, []);

  const addTier = useCallback(() => {
    setForm((prev) => ({ ...prev, tiers: [...prev.tiers, buildEmptyTier()] }));
  }, []);

  const removeTier = useCallback((tierKey) => {
    setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((tier) => tier.key !== tierKey) }));
  }, []);

  const openCommissionRecalculationModal = useCallback(() => {
    if (isCreateMode || !uid) return;
    setShowCommissionRecalculationModal(true);
    setCommissionRecalculationError('');
  }, [isCreateMode, uid]);

  const closeCommissionRecalculationModal = useCallback(() => {
    setShowCommissionRecalculationModal(false);
    setCommissionRecalculationError('');
  }, []);

  const submitCommissionRecalculation = useCallback(async (payload) => {
    if (isCreateMode || !uid) {
      setCommissionRecalculationError('Commission plan uid is required');
      return;
    }

    setIsSaving(true);
    setCommissionRecalculationError('');
    setSuccess('');
    try {
      await adminApi.recalculateCommissionPlanCommissions(token, uid, payload);
      if (!isMountedRef.current) return;
      closeCommissionRecalculationModal();
      setSuccess('Commission plan recalculation queued');
      await loadPlan();
    } catch (requestError) {
      if (!isMountedRef.current) return;
      setCommissionRecalculationError(requestError.message || 'Failed to queue commission plan recalculation');
    } finally {
      if (!isMountedRef.current) return;
      setIsSaving(false);
    }
  }, [isCreateMode, uid, token, closeCommissionRecalculationModal, loadPlan]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();
    if (!form.name.trim()) { setError('Plan name is required'); return; }
    setIsSaving(true); setError(''); setSuccess('');
    try {
      if (isCreateMode) {
        const payload = buildCommissionPlanCreatePayload(form);
        const response = await adminApi.createCommissionPlan(token, payload);
        const createdUid = getCommissionPlanUidFromResponse(response);
        setSuccess('Commission plan created');
        if (createdUid) { navigate(`/commission-plans/${createdUid}`, { replace: true }); }
        else { navigate('/commission-plans', { replace: true }); }
      } else {
        const payload = buildCommissionPlanUpdatePayload(form);
        await adminApi.updateCommissionPlan(token, uid, payload);
        setSuccess('Commission plan updated');
        await loadPlan();
      }
    } catch (requestError) {
      setError(requestError.message || 'Failed to save commission plan');
    } finally { setIsSaving(false); }
  }, [form, isCreateMode, uid, token, navigate, loadPlan]);

  const navigateBack = useCallback(() => { navigate('/commission-plans'); }, [navigate]);

  return {
    form, isCreateMode, isLoading, isSaving, error, success,
    showCommissionRecalculationModal, commissionRecalculationError,
    onFormChange, onTierChange, addTier, removeTier, saveForm, navigateBack,
    openCommissionRecalculationModal, closeCommissionRecalculationModal, submitCommissionRecalculation,
  };
}
